/**
 * 舱内页顶部**来源入口条**(V1 §4.1)—— 三条「我已经有料了」的路。
 *
 * ═══ 它替换的是什么 ═══════════════════════════════════════════════════════════
 * `PosterDashboardPage` 原来的三个页头按钮(从爆款开始 / 自由创作 / 从模板开始)。
 * 那三个按钮把**两种不同的东西**摆成了一排:「从爆款/从模板」是*来源*(我手上已经有料),
 * 「自由创作」是*兜底*(我什么都没有)。而且页头主按钮的视觉权重压过了整面卡墙 ——
 * 用户第一眼看到的是"自由创作",而不是"我要干什么"。
 * 故:来源归来源(这一条),兜底归卡墙末尾那张「我自己描述」卡。
 *
 * ★三条都是**真路**,没有占位:
 *   产品库 → 搭建器(大框一进页就展开,里面就是产品库直选);
 *   爆款   → 情报枢纽(解析出蓝图后带 handoff 回海报);
 *   模板   → 模板库(波A 之后这里是长图模板)。
 * 点了没反应的入口比没有入口更糟,所以不摆「即将」。
 */
import { useState } from "react";
import { Boxes, Flame, LayoutTemplate, type LucideIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS, CARD_WALL_TOUCH } from "@/shared/ui/cardWall";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { templateHandoffState } from "../../../model/home/templateApply";
import { LongImageTemplateWall } from "./LongImageTemplateWall";

interface Entry {
  key: string;
  title: string;
  /** 说清「带进来的是什么」,不说实现。 */
  sub: string;
  Icon: LucideIcon;
  to: string;
  /**
   * 随导航带的 history state。
   * ★产品库那条必须带 `posterEntry:"product"`:搭建器默认会把上次没做完的智能稿恢复出来,
   *   于是点「从产品库开始」看到的是**旧稿**而不是入口承诺的产品选择(codex review P2)。
   *   带上这个标记后,搭建器把它与 preset / 蓝图同级对待 ——「我明确要这套新的」,不吃旧草稿。
   */
  state?: Record<string, unknown>;
}

const ENTRIES: Entry[] = [
  {
    key: "product",
    title: "从产品库开始",
    // ★2026-08-01 改口径:原来写「选一个商品,参数逐字带进来」—— 这条入口的落点是
    //   **长图搭建器**,而「选一个商品带参数」正是商品主图那张卡的承诺,两处说同一句话
    //   用户读不出该点哪个。副标要说清**落到哪**,不只说带了什么。
    sub: "选一个商品,做一张详情长图",
    Icon: Boxes,
    to: "/ai-haibao/detail-long",
    state: { posterEntry: "product" },
  },
  {
    key: "explosive",
    title: "从爆款开始",
    sub: "拆一条爆款,套它的结构",
    Icon: Flame,
    to: "/ai-explosive",
  },
  {
    key: "template",
    // 波A 落地:模板 = 长图模板(V1 埋的口子兑现)。点开**弹层**选(定案 3.8.1
    // 「不必先跳页」),弹层里也给「进模板库看全部」的深链。
    title: "从模板开始",
    sub: "选一套现成长图,换成你的产品",
    Icon: LayoutTemplate,
    to: "", // 弹层,不导航
  },
];

export function PosterSourceEntries() {
  const navigate = useNavigate();
  const [templateOpen, setTemplateOpen] = useState(false);
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {ENTRIES.map(({ key, title, sub, Icon, to, state }) => (
        <button
          key={key}
          type="button"
          data-poster-entry={key}
          onClick={() =>
            key === "template" ? setTemplateOpen(true) : navigate(to, state ? { state } : undefined)
          }
          className={cn(
            "flex items-center gap-3 border border-border/70 bg-card px-3.5 py-3 text-left transition-colors hover:border-primary/60",
            CARD_WALL_RADIUS,
            CARD_WALL_TOUCH,
            "fx-press"
          )}
        >
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-[calc(var(--radius)*0.8)] bg-primary/10 text-primary"
            aria-hidden
          >
            <Icon className="h-[19px] w-[19px]" strokeWidth={1.8} />
          </span>
          <span className="min-w-0">
            <span className="block text-[13.5px] font-semibold">{title}</span>
            <span className="block truncate text-2xs text-muted-foreground">{sub}</span>
          </span>
        </button>
      ))}

      {/* 「从模板开始」弹层:同一份 gallery 组件(定案 3.8.1 不做第四种壳) */}
      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        {/* data 探针:弹层走 Portal 挂 body,URL 又不变 —— 走查若把断言圈在主区容器里
            会误判成「没弹层直接跳了」(0810 E2E LI-TPL-04 即此假阳性)。 */}
        <DialogContent
          data-longimage-template-dialog
          className="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
        >
          <DialogHeader>
            <DialogTitle className="flex items-baseline gap-3">
              从模板开始
              <Link
                to="/ai-haibao/templates"
                className="text-xs font-normal text-primary underline-offset-2 hover:underline"
                onClick={() => setTemplateOpen(false)}
              >
                进模板库看全部 →
              </Link>
            </DialogTitle>
          </DialogHeader>
          <LongImageTemplateWall
            compact
            onPick={(t) => {
              setTemplateOpen(false);
              navigate("/ai-haibao/detail-long", { state: templateHandoffState(t) });
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
